"use client";

import { c as _c } from "react/compiler-runtime";
import { Position } from "@xyflow/react";
import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";
const FALLBACK_HEADER_BOTTOM = 48;
const FALLBACK_TOOLBAR_HEIGHT = 120;
const VIEWPORT_MARGIN = 12;
interface UseNodeToolbarPlacementOptions {
  isVisible: boolean;
  nodeRef?: RefObject<HTMLDivElement | null>;
  offset?: number;
}
function getWorkflowHeaderBottom() {
  const header = document.querySelector<HTMLElement>("[data-workflow-header]");
  return header?.getBoundingClientRect().bottom ?? FALLBACK_HEADER_BOTTOM;
}
export function useNodeToolbarPlacement(t0) {
  const $ = _c(14);
  const {
    isVisible,
    nodeRef,
    offset: t1
  } = t0;
  const offset = t1 === undefined ? 0 : t1;
  const toolbarRef = useRef(null);
  const [position, setPosition] = useState(Position.Top);
  let t2;
  if ($[0] !== isVisible || $[1] !== nodeRef || $[2] !== offset) {
    t2 = () => {
      if (!isVisible || !nodeRef?.current) {
        return;
      }
      const nodeRect = nodeRef.current.getBoundingClientRect();
      const toolbarHeight = toolbarRef.current?.getBoundingClientRect().height ?? FALLBACK_TOOLBAR_HEIGHT;
      const headerBottom = getWorkflowHeaderBottom();
      const viewportTop = window.visualViewport?.offsetTop ?? 0;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;
      const topBoundary = Math.max(headerBottom, viewportTop + VIEWPORT_MARGIN);
      const requiredSpace = toolbarHeight + offset + VIEWPORT_MARGIN;
      const spaceAbove = nodeRect.top - topBoundary;
      const spaceBelow = viewportBottom - VIEWPORT_MARGIN - nodeRect.bottom;
      const nextPosition = spaceAbove >= requiredSpace || spaceAbove >= spaceBelow ? Position.Top : Position.Bottom;
      setPosition(current => current === nextPosition ? current : nextPosition);
    };
    $[0] = isVisible;
    $[1] = nodeRef;
    $[2] = offset;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const updatePosition = t2;
  let t3;
  let t4;
  if ($[4] !== isVisible || $[5] !== nodeRef || $[6] !== updatePosition) {
    t3 = () => {
      if (!isVisible || !nodeRef?.current) {
        return;
      }
      updatePosition();
      const frameId = window.requestAnimationFrame(updatePosition);
      const handleViewportChange = () => updatePosition();
      const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => updatePosition());
      resizeObserver?.observe(nodeRef.current);
      if (toolbarRef.current) {
        resizeObserver?.observe(toolbarRef.current);
      }
      const workflowHeader = document.querySelector("[data-workflow-header]");
      if (workflowHeader) {
        resizeObserver?.observe(workflowHeader);
      }
      window.addEventListener("resize", handleViewportChange);
      window.addEventListener("scroll", handleViewportChange, true);
      window.visualViewport?.addEventListener("resize", handleViewportChange);
      window.visualViewport?.addEventListener("scroll", handleViewportChange);
      return () => {
        window.cancelAnimationFrame(frameId);
        resizeObserver?.disconnect();
        window.removeEventListener("resize", handleViewportChange);
        window.removeEventListener("scroll", handleViewportChange, true);
        window.visualViewport?.removeEventListener("resize", handleViewportChange);
        window.visualViewport?.removeEventListener("scroll", handleViewportChange);
      };
    };
    t4 = [isVisible, nodeRef, updatePosition];
    $[4] = isVisible;
    $[5] = nodeRef;
    $[6] = updatePosition;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useLayoutEffect(t3, t4);
  const t5 = position === Position.Top ? "absolute -bottom-5 left-0 right-0 h-5" : "absolute -top-5 left-0 right-0 h-5";
  const t6 = position === Position.Top ? "slide-in-from-bottom-2" : "slide-in-from-top-2";
  const t7 = position === Position.Top ? "slide-out-to-bottom-2" : "slide-out-to-top-2";
  let t8;
  if ($[9] !== position || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      position,
      toolbarRef,
      bridgeClassName: t5,
      enterAnimationClassName: t6,
      exitAnimationClassName: t7
    };
    $[9] = position;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}